import type { BBox } from "./types";

/** Europe ingest / default map extent. Wider than Belgium so the homepage is never empty. */
export const EUROPE_BBOX: BBox = { west: -25, south: 34, east: 45, north: 72 };

/**
 * First-load camera when we do not have a device location.
 * The ingest bbox runs to 72°N — that frames empty Scandinavia. This is the
 * activity belt: Iberia through the Balkans. Maghreb / Anatolia are omitted.
 */
export const EUROPE_HOME_BBOX: BBox = { west: -10.5, south: 35.5, east: 29.5, north: 55.2 };

/** Belgium frame — country labels and Watch BE alerts. Not the homepage camera. */
export const BELGIUM_BBOX: BBox = { west: 2.52, south: 49.49, east: 6.4, north: 51.51 };

/** MapLibre center is [lng, lat]. */
export const BELGIUM_CENTER: [number, number] = [4.35, 50.64];

export const IBERIA_CENTER: [number, number] = [-7.8, 39.7];

/** Pixels within this distance (and the time window) belong to one incident. */
export const CLUSTER_RADIUS_M = 2500;

/** New detection can join an incident if last_seen is within this gap. */
export const CLUSTER_MAX_GAP_MS = 20 * 60 * 60 * 1000;

/**
 * Smaller countries first so Belgium is not swallowed by France's bbox.
 * Rough administrative boxes — labels only, not legal borders.
 */
const COUNTRY_BOXES: Array<{ code: string; box: BBox }> = [
  { code: "BE", box: BELGIUM_BBOX },
  { code: "LU", box: { west: 5.73, south: 49.44, east: 6.53, north: 50.18 } },
  { code: "NL", box: { west: 3.3, south: 50.75, east: 7.23, north: 53.7 } },
  { code: "CH", box: { west: 5.95, south: 45.82, east: 10.49, north: 47.81 } },
  { code: "AT", box: { west: 9.53, south: 46.37, east: 17.16, north: 49.02 } },
  { code: "PT", box: { west: -9.57, south: 36.96, east: -6.19, north: 42.15 } },
  { code: "GR", box: { west: 19.37, south: 34.8, east: 29.65, north: 41.75 } },
  // After GR so Rhodes stays Greek; south of the TR box so it is not Türkiye.
  { code: "CY", box: { west: 32.2, south: 34.5, east: 34.7, north: 35.75 } },
  { code: "IT", box: { west: 6.62, south: 36.64, east: 18.52, north: 47.09 } },
  { code: "ES", box: { west: -9.3, south: 35.95, east: 3.32, north: 43.79 } },
  { code: "FR", box: { west: -5.14, south: 42.33, east: 8.23, north: 51.09 } },
  { code: "DE", box: { west: 5.87, south: 47.27, east: 15.04, north: 55.06 } },
  { code: "PL", box: { west: 14.12, south: 49.0, east: 24.15, north: 54.84 } },
  { code: "GB", box: { west: -8.18, south: 49.86, east: 1.77, north: 60.86 } },
  { code: "IE", box: { west: -10.48, south: 51.42, east: -5.99, north: 55.39 } },
  { code: "SE", box: { west: 11.11, south: 55.34, east: 24.16, north: 69.06 } },
  { code: "NO", box: { west: 4.64, south: 57.98, east: 31.29, north: 71.18 } },
  { code: "FI", box: { west: 20.65, south: 59.81, east: 31.59, north: 70.09 } },
  { code: "RO", box: { west: 20.26, south: 43.62, east: 29.69, north: 48.27 } },
  { code: "BG", box: { west: 22.36, south: 41.24, east: 28.61, north: 44.22 } },
  { code: "HR", box: { west: 13.49, south: 42.39, east: 19.43, north: 46.55 } },
  { code: "RS", box: { west: 18.84, south: 42.23, east: 23.01, north: 46.19 } },
  { code: "HU", box: { west: 16.11, south: 45.74, east: 22.9, north: 48.59 } },
  { code: "CZ", box: { west: 12.09, south: 48.55, east: 18.86, north: 51.06 } },
  { code: "SK", box: { west: 16.83, south: 47.73, east: 22.57, north: 49.61 } },
  { code: "DK", box: { west: 8.07, south: 54.77, east: 15.2, north: 57.75 } },
  { code: "UA", box: { west: 22.14, south: 44.39, east: 40.23, north: 52.38 } },
  { code: "TR", box: { west: 26.04, south: 35.81, east: 44.82, north: 42.1 } },
  { code: "MA", box: { west: -13.17, south: 27.66, east: -0.99, north: 35.92 } },
  { code: "DZ", box: { west: -8.67, south: 19.06, east: 12.0, north: 37.09 } },
  { code: "TN", box: { west: 7.52, south: 30.23, east: 11.6, north: 37.54 } },
  // After GR/IT/ES/TR so labels do not steal Greek islands, Sicily, or Spain.
  { code: "SY", box: { west: 35.7, south: 32.3, east: 42.4, north: 37.32 } },
  { code: "LB", box: { west: 35.1, south: 33.05, east: 36.65, north: 34.7 } },
  { code: "IL", box: { west: 34.2, south: 29.45, east: 35.9, north: 33.4 } },
  { code: "JO", box: { west: 34.9, south: 29.18, east: 39.3, north: 33.38 } },
  { code: "IQ", box: { west: 38.8, south: 29.1, east: 48.6, north: 37.4 } },
  { code: "IR", box: { west: 44.0, south: 29.0, east: 63.3, north: 39.8 } },
  { code: "GE", box: { west: 39.9, south: 41.05, east: 46.7, north: 43.6 } },
  { code: "AM", box: { west: 43.4, south: 38.8, east: 46.6, north: 41.3 } },
  { code: "AZ", box: { west: 44.8, south: 38.4, east: 50.6, north: 41.9 } },
  { code: "LY", box: { west: 9.3, south: 19.5, east: 25.2, north: 33.2 } },
  { code: "EG", box: { west: 24.7, south: 21.7, east: 36.9, north: 31.7 } },
];

/**
 * Not the public Europe map. UA/RU are industrial + war thermals; the rest sit
 * inside EUROPE_BBOX but are outside the product (Maghreb, Anatolia, Levant).
 */
export const HIDDEN_COUNTRY_CODES = new Set([
  "UA",
  "RU",
  "TR",
  "SY",
  "IQ",
  "IR",
  "DZ",
  "MA",
  "TN",
  "LY",
  "EG",
  "LB",
  "IL",
  "JO",
  "PS",
  "SA",
  "GE",
  "AM",
  "AZ",
]);

/**
 * Russia-only boxes. Not the Ukraine admin box — that rectangle also covers
 * Bucharest. Ukraine is hidden via countryFromLonLat === "UA" / stored country.
 */
const RUSSIA_HIDE_BOXES: BBox[] = [
  { west: 19.55, south: 54.3, east: 22.9, north: 55.4 },
  { west: 29.4, south: 59.6, east: 31.7, north: 60.35 },
  { west: 31.7, south: 52.4, east: 45, north: 72 },
  { west: 36.5, south: 41.15, east: 45, north: 52.4 },
];

/**
 * Lat/lon hide when country is missing or wrong. GR's box reaches Istanbul;
 * ES reaches Algiers; IT reaches Tunis. Greek islands stay west/south of these.
 */
const OUTSIDE_EUROPE_BOXES: BBox[] = [
  // Anatolia east of Kastellorizo (29.58°E). Rhodes 28.2°E stays out.
  { west: 29.7, south: 35.8, east: 45, north: 42.5 },
  // Turkish Thrace / Istanbul, east of Alexandroupoli (25.87°E).
  { west: 26.55, south: 40.55, east: 29.7, north: 42.3 },
  // Izmir / Aegean Turkey, north of Samos (37.76°N) and east of Chios (26.14°E).
  { west: 26.9, south: 37.95, east: 29.7, north: 40.55 },
  // Gallipoli / Çanakkale, north of Lesbos (~39.4°N).
  { west: 25.7, south: 39.85, east: 26.9, north: 40.55 },
  // Çeşme peninsula, east of Chios town.
  { west: 26.25, south: 38.15, east: 26.9, north: 38.55 },
  // Bodrum, north of Kos (~36.89°N).
  { west: 27.45, south: 37.0, east: 28.25, north: 37.95 },
  // Marmaris–Fethiye, east of Rhodes (28.22°E) and north of Rhodes town.
  { west: 28.25, south: 36.55, east: 29.7, north: 37.95 },
  // Levant + Iraq, east of Cyprus (east tip ~34.6°E).
  { west: 34.9, south: 29, east: 49, north: 37.6 },
  // Eastern Algeria + Tunisia, east of Spain's Mediterranean and west of Pantelleria.
  { west: 3.4, south: 19, east: 11.55, north: 37.15 },
  // Morocco + western Algeria, south of Gibraltar / Cadiz.
  { west: -13.2, south: 19, east: 3.4, north: 35.94 },
  // Algiers strip (often stored as ES). Almeria is west of -1.2°; Cartagena is north of 37.12°.
  { west: -1.2, south: 35.94, east: 3.4, north: 37.12 },
];

export function inBBox(lat: number, lon: number, box: BBox): boolean {
  return lon >= box.west && lon <= box.east && lat >= box.south && lat <= box.north;
}

/** True outside the public Europe map — ingest, live map, catalog, and alerts. */
export function isHiddenFireRegion(lat: number, lon: number, country?: string | null): boolean {
  if (country && HIDDEN_COUNTRY_CODES.has(country)) return true;
  if (HIDDEN_COUNTRY_CODES.has(countryFromLonLat(lat, lon))) return true;
  if (RUSSIA_HIDE_BOXES.some((box) => inBBox(lat, lon, box))) return true;
  return OUTSIDE_EUROPE_BOXES.some((box) => inBBox(lat, lon, box));
}

function boxesOverlap(a: BBox, b: BBox): boolean {
  return a.west <= b.east && a.east >= b.west && a.south <= b.north && a.north >= b.south;
}

/** Ukraine admin box minus earlier COUNTRY_BOXES (RO/PL/HU/SK) so Bucharest stays visible. */
function ukraineDetectionSql(p: string): string {
  const uaIdx = COUNTRY_BOXES.findIndex((c) => c.code === "UA");
  const ua = COUNTRY_BOXES[uaIdx];
  if (!ua) return "";
  const inUa = `${p}lat BETWEEN ${ua.box.south} AND ${ua.box.north} AND ${p}lon BETWEEN ${ua.box.west} AND ${ua.box.east}`;
  const earlier = COUNTRY_BOXES.slice(0, uaIdx)
    .filter((c) => boxesOverlap(c.box, ua.box))
    .map((c) => `(${p}lat BETWEEN ${c.box.south} AND ${c.box.north} AND ${p}lon BETWEEN ${c.box.west} AND ${c.box.east})`)
    .join(" OR ");
  return earlier ? `(${inUa} AND NOT (${earlier}))` : `(${inUa})`;
}

function bboxSql(p: string, boxes: BBox[]): string {
  return boxes
    .map((b) => `(${p}lat BETWEEN ${b.south} AND ${b.north} AND ${p}lon BETWEEN ${b.west} AND ${b.east})`)
    .join(" OR ");
}

/** D1 WHERE fragment for incidents (have country) or detections (lat/lon only). */
export function hiddenRegionSql(kind: "incident" | "detection" = "incident", alias = ""): string {
  const p = alias ? `${alias}.` : "";
  // Lat/lon boxes catch Istanbul stored as GR and Algiers stored as ES.
  const boxes = bboxSql(p, [...RUSSIA_HIDE_BOXES, ...OUTSIDE_EUROPE_BOXES]);
  const codes = [...HIDDEN_COUNTRY_CODES]
    .sort()
    .map((c) => `'${c}'`)
    .join(", ");
  const country = kind === "incident" ? `${p}country IN (${codes}) OR ` : "";
  // Detections have no country column — encode the same first-match UA rule as countryFromLonLat.
  const ua = kind === "detection" ? ` OR ${ukraineDetectionSql(p)}` : "";
  return `NOT (${country}${boxes}${ua})`;
}

/**
 * Ingest domain for fire pixels. EUROPE_BBOX is a rectangle — it includes
 * Maghreb, Anatolia, and the Levant. Those are dropped here (and again at
 * read time) so Cyprus and Crete stay in without keeping Ankara or Algiers.
 */
export function inEuropeFireDomain(lat: number, lon: number, box: BBox = EUROPE_BBOX): boolean {
  if (!inBBox(lat, lon, box)) return false;
  if (isHiddenFireRegion(lat, lon)) return false;
  return true;
}

export function countryFromLonLat(lat: number, lon: number): string {
  for (const { code, box } of COUNTRY_BOXES) {
    if (inBBox(lat, lon, box)) return code;
  }
  return "EU";
}

const COUNTRY_NAMES: Record<string, string> = {
  BE: "Belgium",
  LU: "Luxembourg",
  NL: "Netherlands",
  CH: "Switzerland",
  AT: "Austria",
  PT: "Portugal",
  GR: "Greece",
  CY: "Cyprus",
  IT: "Italy",
  ES: "Spain",
  FR: "France",
  DE: "Germany",
  PL: "Poland",
  GB: "United Kingdom",
  IE: "Ireland",
  SE: "Sweden",
  NO: "Norway",
  FI: "Finland",
  RO: "Romania",
  BG: "Bulgaria",
  HR: "Croatia",
  RS: "Serbia",
  HU: "Hungary",
  CZ: "Czechia",
  SK: "Slovakia",
  DK: "Denmark",
  UA: "Ukraine",
  RU: "Russia",
  TR: "Türkiye",
  MA: "Morocco",
  DZ: "Algeria",
  TN: "Tunisia",
  SY: "Syria",
  LB: "Lebanon",
  IL: "Israel",
  JO: "Jordan",
  IQ: "Iraq",
  IR: "Iran",
  GE: "Georgia",
  AM: "Armenia",
  AZ: "Azerbaijan",
  LY: "Libya",
  EG: "Egypt",
  EU: "Europe",
};

export function countryName(code: string): string {
  return COUNTRY_NAMES[code] ?? code;
}

/** Web Mercator pixel at a zoom (256px tiles). Replay canvas uses this so dots sit on the basemap. */
export function lonLatToWorldPx(lon: number, lat: number, z: number): [number, number] {
  const n = 2 ** z * 256;
  const x = ((lon + 180) / 360) * n;
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const s = Math.sin((clamped * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n;
  return [x, y];
}

export function nearestByHaversine<T extends { lat: number; lon: number }>(
  items: T[],
  lat: number,
  lon: number,
  maxM: number,
): T | null {
  let best: T | null = null;
  let bestD = maxM;
  for (const item of items) {
    const d = haversineM(lat, lon, item.lat, item.lon);
    if (d < bestD) {
      bestD = d;
      best = item;
    }
  }
  return best;
}

/** Click-to-incident radius grows when zoomed out so a heat blob stays hittable. */
export function clickRadiusM(zoom: number): number {
  return Math.max(5000, Math.min(12000, 10000 * 2 ** (8 - zoom)));
}

/** Other incidents within `maxM` — display grouping, not a merge of IDs. */
export function nearbyIncidents<T extends { id: string; lat: number; lon: number }>(
  items: T[],
  origin: T,
  maxM = 6000,
  limit = 4,
): Array<T & { distance_m: number }> {
  const out: Array<T & { distance_m: number }> = [];
  for (const item of items) {
    if (item.id === origin.id) continue;
    const d = haversineM(origin.lat, origin.lon, item.lat, item.lon);
    if (d <= maxM) out.push({ ...item, distance_m: d });
  }
  out.sort((a, b) => a.distance_m - b.distance_m);
  return out.slice(0, limit);
}

/**
 * Destination [lon, lat] after travelling `distKm` along `bearingDeg` (0 = north).
 * Used to draw the downwind shaft on a selected incident.
 */
export function destinationLonLat(lat: number, lon: number, bearingDeg: number, distKm: number): [number, number] {
  const R = 6371;
  const brng = (bearingDeg * Math.PI) / 180;
  const φ1 = (lat * Math.PI) / 180;
  const λ1 = (lon * Math.PI) / 180;
  const δ = distKm / R;
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(brng));
  const λ2 =
    λ1 + Math.atan2(Math.sin(brng) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2));
  return [((λ2 * 180) / Math.PI + 540) % 360 - 180, (φ2 * 180) / Math.PI];
}

/** Meteorological wind is FROM; flames/smoke are pushed the opposite way. */
export function windToBearing(fromDeg: number): number {
  return (fromDeg + 180) % 360;
}

/** Initial bearing from A to B (deg clockwise from north). */
export function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Monotone-chain hull. Input and output are [lon, lat]. */
export function convexHullLonLat(points: Array<[number, number]>): Array<[number, number]> {
  const uniq = [...new Map(points.map((p) => [`${p[0].toFixed(5)},${p[1].toFixed(5)}`, p])).values()];
  if (uniq.length <= 2) return uniq;
  uniq.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: [number, number], a: [number, number], b: [number, number]) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Array<[number, number]> = [];
  for (const p of uniq) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Array<[number, number]> = [];
  for (let i = uniq.length - 1; i >= 0; i--) {
    const p = uniq[i]!;
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/**
 * Closed [lon, lat] ring around a fire's heat cells, padded so it traces
 * the glow instead of the centroid. One or two points become a circle.
 */
export function paddedHeatOutline(points: Array<[number, number]>, padKm = 1.2): Array<[number, number]> {
  if (points.length === 0) return [];
  const hull = convexHullLonLat(points);
  let cx = 0;
  let cy = 0;
  for (const [lon, lat] of hull) {
    cx += lon;
    cy += lat;
  }
  cx /= hull.length;
  cy /= hull.length;

  if (hull.length < 3) {
    let r = padKm;
    if (hull.length === 2) {
      r = haversineM(hull[0]![1], hull[0]![0], hull[1]![1], hull[1]![0]) / 2000 + padKm;
    }
    const ring: Array<[number, number]> = [];
    for (let i = 0; i < 24; i++) {
      const [lon, lat] = destinationLonLat(cy, cx, i * 15, r);
      ring.push([lon, lat]);
    }
    ring.push(ring[0]!);
    return ring;
  }

  const out = hull.map(([lon, lat]) => {
    const dKm = haversineM(cy, cx, lat, lon) / 1000;
    const brng = bearingDeg(cy, cx, lat, lon);
    const [nlon, nlat] = destinationLonLat(cy, cx, brng, dKm + padKm);
    return [nlon, nlat] as [number, number];
  });
  out.push(out[0]!);
  return out;
}

/** SW/NE around heat cells (or the pin), padded so a pinprick still fills a close camera. */
export function heatFrameBounds(
  points: Array<[number, number]>,
  fallback?: [number, number],
  padKm = 1.8,
): [[number, number], [number, number]] | null {
  const seed = points.length > 0 ? points : fallback ? [fallback] : [];
  const ring = paddedHeatOutline(seed, padKm);
  if (ring.length < 2) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const [lon, lat] of ring) {
    if (lon < west) west = lon;
    if (lat < south) south = lat;
    if (lon > east) east = lon;
    if (lat > north) north = lat;
  }
  if (![west, south, east, north].every(Number.isFinite)) return null;
  return [
    [west, south],
    [east, north],
  ];
}

/** Extra km past the heat hull so a mapped scar beside the glow still fits. */
export const EVENT_BURNED_PAD_KM = 4;

/** Snap to ~2 km so a drifting centroid does not refetch the WMS every paint. */
export const EVENT_BURNED_QUANTIZE_DEG = 0.02;

/** Heat trail (or the pin) plus pad — the moving box for the local burned overlay. */
export function eventBurnedBBox(
  points: Array<[number, number]>,
  fallback: [number, number],
): BBox | null {
  const frame = heatFrameBounds(points, fallback, EVENT_BURNED_PAD_KM);
  if (!frame) return null;
  const [[west, south], [east, north]] = frame;
  return { west, south, east, north };
}

/** Expand to the next grid cell so small hull growth stays on the same image. */
export function quantizeLonLatBBox(box: BBox, stepDeg = EVENT_BURNED_QUANTIZE_DEG): BBox {
  const step = stepDeg > 0 ? stepDeg : EVENT_BURNED_QUANTIZE_DEG;
  return {
    west: Math.floor(box.west / step) * step,
    south: Math.floor(box.south / step) * step,
    east: Math.ceil(box.east / step) * step,
    north: Math.ceil(box.north / step) * step,
  };
}

/** Upper bound on cluster radius from a bbox — cheaper than scanning every pixel. */
export function bboxRadiusM(
  lat: number,
  lon: number,
  latMin: number | null | undefined,
  latMax: number | null | undefined,
  lonMin: number | null | undefined,
  lonMax: number | null | undefined,
): number {
  if (latMin == null || latMax == null || lonMin == null || lonMax == null) return 0;
  if (![latMin, latMax, lonMin, lonMax].every(Number.isFinite)) return 0;
  return Math.max(
    haversineM(lat, lon, latMin, lonMin),
    haversineM(lat, lon, latMin, lonMax),
    haversineM(lat, lon, latMax, lonMin),
    haversineM(lat, lon, latMax, lonMax),
  );
}

/** Haversine distance in metres. */
export function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** ~2.2 km grid cell so clustering is O(n) instead of O(n²). */
const GEO_CELL_DEG = 0.02;

export function geoCell(lat: number, lon: number): string {
  return `${Math.floor(lat / GEO_CELL_DEG)}_${Math.floor(lon / GEO_CELL_DEG)}`;
}

/**
 * Every cell a fire of this radius can still claim. Large High Fens events
 * must be indexed west of their centroid or Jalhay-shaped pixels start a sibling.
 */
export function cellsCovered(lat: number, lon: number, radiusM: number): string[] {
  const reach = Math.min(Math.max(radiusM, CLUSTER_RADIUS_M), 20_000);
  const dLat = reach / 111_000;
  const dLon = reach / (111_000 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
  const lat0 = Math.floor((lat - dLat) / GEO_CELL_DEG);
  const lat1 = Math.floor((lat + dLat) / GEO_CELL_DEG);
  const lon0 = Math.floor((lon - dLon) / GEO_CELL_DEG);
  const lon1 = Math.floor((lon + dLon) / GEO_CELL_DEG);
  const out: string[] = [];
  for (let i = lat0; i <= lat1; i++) {
    for (let j = lon0; j <= lon1; j++) {
      out.push(`${i}_${j}`);
    }
  }
  return out;
}

export function neighborCells(cell: string): string[] {
  const [latI, lonI] = cell.split("_").map(Number);
  const out: string[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      out.push(`${latI + dy}_${lonI + dx}`);
    }
  }
  return out;
}

/** Web Mercator XYZ tile → EPSG:3857 bbox for WMS GetMap. */
export function xyzToMercatorBBox(z: number, x: number, y: number): [number, number, number, number] {
  const n = 2 ** z;
  const lonMin = (x / n) * 360 - 180;
  const lonMax = ((x + 1) / n) * 360 - 180;
  const latMax = (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI;
  const latMin = (Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * 180) / Math.PI;
  return [...lonLatToMercator(lonMin, latMin), ...lonLatToMercator(lonMax, latMax)];
}

/** Lon/lat → EPSG:3857 metres. Used for EFFIS GetFeatureInfo around a click. */
export function lonLatToMercator(lon: number, lat: number): [number, number] {
  const x = (lon * 20037508.34) / 180;
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const y =
    (Math.log(Math.tan(((90 + clamped) * Math.PI) / 360)) * 20037508.34) / Math.PI;
  return [x, y];
}

/** Inverse of lonLatToMercator — corners for a MapLibre image overlay. */
export function mercatorToLonLat(x: number, y: number): [number, number] {
  const lon = (x * 180) / 20037508.34;
  const lat = (Math.atan(Math.sinh((y * Math.PI) / 20037508.34)) * 180) / Math.PI;
  return [lon, lat];
}
